import {
  CaretLeft,
  CaretRight,
  Lock,
  LockOpen,
  Warning,
  X,
} from '@phosphor-icons/react';
import { DotsThreeVertical } from '@phosphor-icons/react/dist/ssr';
import { HttpStatusCode } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { DebitCardWithTruck, Entity } from '../../../../../../types/apiTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';
import AddNewCardModal from './modals/AddNewCardModal';
import { useSwipeable } from 'react-swipeable';
import ActivateNewCardModal from './modals/ActivateNewCardModal';
import { Modal } from 'antd';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';
import { useQuery, useQueryClient } from '@tanstack/react-query';

function CardWidget({
  account_id,
  isPreActive = false,
}: {
  account_id: string;
  isPreActive?: boolean;
}) {
  const { screenView } = useScreenView();
  const [loadingFreeze, setLoadingFreeze] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [cardImageLoading, setCardImageLoading] = useState(false);
  const [currCardIdx, setCurrCardIdx] = useState(0);
  const [currCardImage, setCurrCardImage] = useState<string | null>(null);
  const { callProtectedApi } = useApi();
  const { userId, userRecord } = useUser();
  const queryClient = useQueryClient();
  const [popupVisible, setPopupVisible] = useState(false);
  const [newCardPopupVisible, setNewCardPopupVisible] = useState(false);
  const [activateCardPopupVisible, setActivateCardPopupVisible] =
    useState(false);
  const [hideCardDetails, setHideCardDetails] = useState(true);
  const popupRef = useRef<HTMLDivElement>(null);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(
    null
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextCardIdx, setNextCardIdx] = useState<number | null>(null);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const shouldFetchRealData = !!userId && !isPreActive;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setPopupVisible(false);
      }
    };

    if (popupVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupVisible]);

  const fetchCards = async (customer_id: string) => {
    const response = await callProtectedApi(
      `/debitCards/list/${customer_id}`,
      'get',
      {},
      'Error occurred while fetching your cards.'
    );
    return response?.data?.data?.debit_cards;
  };

  const fetchCardImage = async (debit_card_id: string) => {
    if (!shouldFetchRealData) {
      toast.error('Complete your onboarding steps to see card details.');
      return;
    }
    setCardImageLoading(true);
    try {
      const response = await callProtectedApi(
        `/debitCards/${debit_card_id}/image`,
        'get',
        {},
        'Error occurred while fetching your card details.'
      );
      const image = response?.data?.data?.card_image_link;

      if (
        image &&
        image?.length > 0 &&
        response?.status === HttpStatusCode.Ok
      ) {
        setCurrCardImage(image);
      }
    } catch (error: any) {
      console.error('Error occurred while fetching your card image.');
    } finally {
      setCardImageLoading(false);
    }
  };

  const { data: cards, isLoading: loadingCards } = useQuery<
    DebitCardWithTruck[]
  >({
    queryKey: ['fetchCards', userId],
    queryFn: () => fetchCards(userId ?? ''),
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // Derive currCard from cards and currCardIdx
  const currCard = useMemo(() => {
    if (!cards || cards.length === 0) return null;
    const validIdx = Math.min(currCardIdx, cards.length - 1);
    return cards[validIdx] || null;
  }, [cards, currCardIdx]);

  const freezeCard = async (debit_card_id: string, frozen: boolean) => {
    setLoadingFreeze(true);
    try {
      const response = await callProtectedApi(
        `/debitCards/freeze`,
        'post',
        {
          is_frozen: frozen,
          debit_card_id: debit_card_id,
        },
        `Error occurred while ${frozen ? 'freezing' : 'unfreezing'} your card.`
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success(`Card successfully ${frozen ? 'frozen' : 'unfrozen'}.`);
        setCurrCardImage(null);
        await queryClient.invalidateQueries({
          queryKey: ['fetchCards', userId],
        });
      }
    } catch (error: any) {
      console.error(
        `Error occurred while ${frozen ? 'freezing' : 'unfreezing'} your card.`
      );
    } finally {
      setLoadingFreeze(false);
    }
  };

  const showCancelConfirm = (
    debit_card_id: string,
    last_four_digits: string
  ) => {
    Modal.confirm({
      title: `Are you sure you want to cancel your card ending in **** ${last_four_digits}?`,
      content: `Once cancelled, your card will stop working immediately. This action is permanent and cannot be undone.`,
      okText: 'Yes, Cancel Card',
      okType: 'danger',
      cancelText: 'No, Go Back',
      async onOk() {
        await cancelCard(debit_card_id);
        setPopupVisible(false);
      },
    });
  };

  const cancelCard = async (debit_card_id: string) => {
    setLoadingCancel(true);
    try {
      const response = await callProtectedApi(
        `/debitCards/cancel`,
        'post',
        {
          debit_card_id: debit_card_id,
        },
        `Error occurred while cancelling your card.`
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success(`Card successfully cancelled.`);
        setCurrCardImage(null);
        await queryClient.invalidateQueries({
          queryKey: ['fetchCards', userId],
        });
      }
    } catch (error: any) {
      console.error(`Error occurred while cancelling your card.`);
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleImageError = async () => {
    if (currCard?.debit_card_id) {
      await fetchCardImage(currCard.debit_card_id);
    }
  };

  const clamp = (v: number, min: number, max: number) =>
    Math.min(max, Math.max(min, v));

  const swipeHandlers = useSwipeable({
    onSwiping: (e) => {
      if (!cards || cards.length <= 1) return;
      setIsSwiping(true);
      // normalize deltaX into [-1, 1] range (feels right on both touch & mouse)
      const p = clamp(e.deltaX / 180, -1, 1);
      setSwipeProgress(p);
    },
    onSwiped: () => {
      setIsSwiping(false);
      setSwipeProgress(0);
    },
    onSwipedLeft: () => {
      if (cards && cards.length > 1 && !isTransitioning) {
        setIsTransitioning(true);
        setSlideDirection('left');
        setHideCardDetails(true);
        const next = (currCardIdx + 1) % cards.length;
        setNextCardIdx(next);
        setCurrCardImage(null);
        setTimeout(() => {
          setCurrCardIdx(next);
          setNextCardIdx(null);
          setSlideDirection(null);
          setIsTransitioning(false);
        }, 300);
      }
    },
    onSwipedRight: () => {
      if (cards && cards.length > 1 && !isTransitioning) {
        setIsTransitioning(true);
        setSlideDirection('right');
        setHideCardDetails(true);
        const next = currCardIdx - 1 < 0 ? cards.length - 1 : currCardIdx - 1;
        setNextCardIdx(next);
        setCurrCardImage(null);
        setTimeout(() => {
          setCurrCardIdx(next);
          setNextCardIdx(null);
          setSlideDirection(null);
          setIsTransitioning(false);
        }, 300);
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  const activeCardCountByTruckId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of cards ?? []) {
      const cancelled = c.is_cancelled === true;
      if (!cancelled && c.truck_id) {
        counts.set(c.truck_id, (counts.get(c.truck_id) ?? 0) + 1);
      }
    }
    return counts;
  }, [cards]);

  // Active business cards (no truck) count toward the per-customer cap
  const activeBusinessCardCount = useMemo(
    () => (cards ?? []).filter((c) => !c.is_cancelled && !c.truck_id).length,
    [cards]
  );

  const renderCardDisplay = () => {
    const cardWidth = 320;
    const cardHeight = 200;

    return (
      <div
        className={`relative flex ${screenView === 'mobile' ? 'w-[320px]' : 'w-[320px]'} flex-col items-center justify-center py-4`}
      >
        <style>
          {`
            @keyframes slideFromRight {
              0% { transform: translateX(400px); opacity: 0; }
              20% { opacity: 1; }
              100% { transform: translateX(0); opacity: 1; }
            }
            @keyframes slideFromLeft {
              0% { transform: translateX(-400px); opacity: 0; }
              20% { opacity: 1; }
              100% { transform: translateX(0); opacity: 1; }
            }
            @keyframes slideToLeft {
              0% { transform: translateX(0); opacity: 1; }
              80% { opacity: 0; }
              100% { transform: translateX(-400px); opacity: 0; }
            }
            @keyframes slideToRight {
              0% { transform: translateX(0); opacity: 1; }
              80% { opacity: 0; }
              100% { transform: translateX(400px); opacity: 0; }
            }
          `}
        </style>

        {cards && cards.length > 1 && screenView !== 'mobile' && (
          <CaretLeft
            className="absolute -left-6 top-[40%] z-30 hover:cursor-pointer hover:opacity-60"
            size={24}
            weight="bold"
            style={{ opacity: 0.7, transition: 'opacity 200ms' }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '.7')}
            onClick={() => {
              if (!isTransitioning) {
                setIsTransitioning(true);
                setSlideDirection('right');
                setHideCardDetails(true);

                let next = currCardIdx - 1;
                if (next === -1) {
                  next = cards.length - 1;
                }

                setNextCardIdx(next);
                setCurrCardImage(null);

                setTimeout(() => {
                  setCurrCardIdx(next);
                  setNextCardIdx(null);
                  setSlideDirection(null);
                  setIsTransitioning(false);
                }, 300);
              }
            }}
          />
        )}

        {cards && cards.length > 1 && screenView !== 'mobile' && (
          <CaretRight
            className="absolute -right-6 top-[40%] z-30 hover:cursor-pointer hover:opacity-60"
            size={24}
            weight="bold"
            style={{ opacity: 0.7, transition: 'opacity 200ms' }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '.7')}
            onClick={() => {
              if (!isTransitioning) {
                setIsTransitioning(true);
                setSlideDirection('left');
                setHideCardDetails(true);

                const next = (currCardIdx + 1) % cards.length;
                setNextCardIdx(next);
                setCurrCardImage(null);

                setTimeout(() => {
                  setCurrCardIdx(next);
                  setNextCardIdx(null);
                  setSlideDirection(null);
                  setIsTransitioning(false);
                }, 300);
              }
            }}
          />
        )}

        <div
          className={`relative h-[${cardHeight}px] w-[${cardWidth}px] ${screenView === 'mobile' ? 'overflow-hidden' : ''}`}
        >
          {/* Current Card */}
          <div
            {...(screenView === 'mobile' ? swipeHandlers : {})}
            className={`group absolute left-0 top-0 h-[${cardHeight}px] w-[${cardWidth}px] rounded-lg bg-primary ${
              isTransitioning ? 'transition-all duration-300 ease-in-out' : ''
            }`}
            style={{
              animation: isTransitioning
                ? `slideTo${slideDirection === 'left' ? 'Left' : 'Right'} 300ms ease-out forwards`
                : undefined,
              willChange: 'transform, opacity',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              zIndex: isTransitioning ? 1 : 2,

              // ⬇️ subtle tilt + parallax while swiping
              transform: isSwiping
                ? `translateX(${swipeProgress * 10}px) rotateY(${swipeProgress * -5}deg)`
                : undefined,
              transition: isSwiping
                ? 'transform 40ms linear'
                : 'transform 180ms ease-out',
            }}
          >
            {cardImageLoading && (
              <div className="absolute inset-0 z-40 flex items-center justify-center rounded-lg bg-black bg-opacity-40">
                <ClipLoader color="#ffffff" size={40} />
              </div>
            )}
            {currCard?.frozen && (
              <div className="absolute bottom-0 left-0 z-30 flex h-full w-full flex-col justify-between">
                <div className="inset-0 flex h-full w-full items-center justify-center rounded-t-lg bg-black bg-opacity-60 align-middle">
                  <div className="flex h-[40px] w-[110px] flex-row items-center justify-center space-x-2 rounded-[4px] border-[1px] border-white border-opacity-[24%] bg-[#25343C] bg-opacity-[40%] p-2 align-middle backdrop-blur-[24%]">
                    <Lock size={24} className="text-white" />{' '}
                    <p className="text-[16px] text-white">Frozen</p>
                  </div>
                </div>
                <div className="flex h-[65px] w-full flex-row items-center justify-between space-x-2 rounded-b-lg border-[1px] border-[#0390F4] bg-[#E6F4FE] px-3 py-2 align-middle">
                  <Lock size={24} className="text-[#0390F4]" weight="bold" />{' '}
                  <p className="text-[14px] text-primary">
                    This card is frozen. You can unfreeze it in the card
                    settings.
                  </p>
                </div>
              </div>
            )}
            {currCard?.physical_card_activated_timestamp === null && (
              <div className="absolute bottom-0 left-0 z-30 flex h-full w-full flex-col justify-between">
                <div className="inset-0 flex h-full w-full rounded-t-lg bg-black bg-opacity-30 p-3 align-middle">
                  <div className="flex h-[32px] flex-row items-center justify-center rounded-[8px] border-[1px] border-[#E2660C] bg-[#FCF0E7] px-2 py-1 align-middle backdrop-blur-[24%]">
                    <Warning size={24} className="mr-1.5 text-[#E2660C]" />
                    <p className="text-[12px] text-primary">
                      Physical card is not activated
                    </p>
                  </div>
                </div>
              </div>
            )}
            {hideCardDetails &&
              !currCard?.frozen &&
              !isPreActive &&
              currCard?.debit_card_id && (
                <div className="absolute inset-0 z-30 flex items-center justify-center rounded-lg bg-black bg-opacity-60 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <button
                    className="flex items-center justify-center space-x-2 border-none text-lg font-semibold text-white"
                    onClick={async () => {
                      await fetchCardImage(currCard.debit_card_id);
                      setHideCardDetails(false);
                    }}
                  >
                    Click to see details
                  </button>
                </div>
              )}
            {!currCardImage ? (
              <div className="h-full w-full">
                <img
                  src="/bp-tach-card.webp"
                  alt="card-image"
                  className={`absolute left-0 top-0 rounded-lg object-cover h-[${cardHeight}px] w-[${cardWidth}px]`}
                />
              </div>
            ) : (
              <div className="">
                <img
                  src={currCardImage}
                  alt="card-image"
                  className={`absolute left-0 top-0 rounded-lg object-cover h-[${cardHeight}px] w-[${cardWidth}px]`}
                  onError={handleImageError}
                />
              </div>
            )}
          </div>

          {/* Next Card (for animation) */}
          {nextCardIdx !== null && cards && (
            <div
              className={`group absolute left-0 top-0 h-[${cardHeight}px] w-[${cardWidth}px] rounded-lg bg-primary`}
              style={{
                animation: isTransitioning
                  ? `slideFrom${slideDirection === 'left' ? 'Right' : 'Left'} 300ms ease-out forwards`
                  : undefined,
                opacity: isTransitioning ? 0 : 1, // start hidden, then animate in
                willChange: 'transform, opacity',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                zIndex: isTransitioning ? 2 : 1,
              }}
            >
              <div className="h-full w-full hover:cursor-pointer">
                <img
                  src="/bp-tach-card.webp"
                  alt="card-image"
                  className={`absolute left-0 top-0 rounded-lg object-cover h-[${cardHeight}px] w-[${cardWidth}px]`}
                />
              </div>
            </div>
          )}
        </div>

        {cards && cards.length > 1 && (
          <div className="mt-4 flex w-full flex-row items-center justify-center space-x-2 align-middle">
            {cards.map((_, idx) => {
              return (
                <div
                  key={idx}
                  className={`h-[8px] w-[8px] rounded-full ${idx === currCardIdx ? 'bg-accent' : 'bg-[#FDEACB]'}`}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderScreenView = (screenView: string) => {
    const containerClasses = {
      desktop:
        'flex h-[385px] w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle',
      tablet:
        'flex h-[385px] w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle',
      mobile:
        'flex h-[385px] w-full max-w-[343px] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle',
    };

    return (
      <div
        className={
          containerClasses[screenView as keyof typeof containerClasses]
        }
      >
        {newCardPopupVisible && (
          <AddNewCardModal
            onClose={() => {
              setNewCardPopupVisible(false);
              void queryClient.invalidateQueries({
                queryKey: ['fetchCards', userId],
              });
            }}
            isOpen={newCardPopupVisible}
            account_id={account_id}
            activeCardCountByTruckId={activeCardCountByTruckId}
            activeBusinessCardCount={activeBusinessCardCount}
          />
        )}
        {activateCardPopupVisible && (
          <ActivateNewCardModal
            onClose={() => setActivateCardPopupVisible(false)}
            isOpen={activateCardPopupVisible}
            debit_card_id={currCard?.debit_card_id ?? ''}
          />
        )}
        <div className="relative flex max-h-[57px] w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
          <div className="flex flex-row items-center space-x-2">
            <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
              Your TACH Cards
            </h2>
            <p className="text-[14px] text-graysecondary">
              {currCard?.truck?.truck_display_name ?? 'Business'}
            </p>
          </div>
          {!isPreActive && (
            <DotsThreeVertical
              onClick={() => setPopupVisible(true)}
              size={screenView === 'desktop' ? 24 : 30}
              className="hover:cursor-pointer hover:opacity-60"
              weight="bold"
            />
          )}
          {popupVisible && (
            <div
              ref={popupRef}
              className="absolute right-0 top-[2.85rem] z-40 w-56 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
            >
              <div className="flex flex-col">
                {currCard?.physical_card_activated_timestamp === null && (
                  <button
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setPopupVisible(false);
                      setActivateCardPopupVisible(true);
                    }}
                    className="flex w-full flex-row items-center space-x-1 rounded-lg px-4 py-2 text-left align-middle text-orange-600 hover:bg-gray-100"
                  >
                    <img
                      alt="card-add"
                      src="/card-tick.svg"
                      className="h-[20px] w-[20px]"
                      style={{
                        filter:
                          'invert(30%) sepia(100%) saturate(800%) hue-rotate(359deg) brightness(97%) contrast(101%)',
                      }}
                    />
                    <p>Activate Physical Card</p>
                  </button>
                )}
                <button
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setPopupVisible(false);
                    setNewCardPopupVisible(true);
                  }}
                  className="flex w-full flex-row items-center space-x-1 rounded-lg px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                >
                  <img
                    alt="card-add"
                    src="/card-add.svg"
                    className="h-[20px] w-[20px]"
                  />
                  <p>Add New Card</p>
                </button>
                {!currCard?.frozen ? (
                  <button
                    onClick={async (e: any) => {
                      e.stopPropagation();
                      await freezeCard(currCard?.debit_card_id ?? '', true);
                      setPopupVisible(false);
                    }}
                    className="flex w-full flex-row items-center space-x-1 rounded-lg px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                  >
                    <Lock size={20} />
                    <p>Freeze Debit Card</p>
                    {loadingFreeze && (
                      <ClipLoader
                        color="#1D213B"
                        loading={loadingFreeze}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        className="h-full w-full justify-center"
                      />
                    )}
                  </button>
                ) : (
                  <button
                    onClick={async (e: any) => {
                      e.stopPropagation();
                      await freezeCard(currCard?.debit_card_id ?? '', false);
                      setPopupVisible(false);
                    }}
                    className="flex w-full flex-row items-center space-x-1 rounded-lg px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                  >
                    <LockOpen size={20} />
                    <p>Unfreeze Debit Card</p>
                    {loadingFreeze && (
                      <ClipLoader
                        color="#1D213B"
                        loading={loadingFreeze}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        className="h-full w-full justify-center"
                      />
                    )}
                  </button>
                )}
                <button
                  onClick={async (e: any) => {
                    e.stopPropagation();
                    await showCancelConfirm(
                      currCard?.debit_card_id ?? '',
                      currCard?.last_four_digits ?? ''
                    );
                  }}
                  className="flex w-full flex-row items-center space-x-1 rounded-lg px-4 py-2 text-left align-middle text-danger hover:bg-gray-100"
                >
                  <X
                    size={20}
                    className="h-[20px] min-h-[20px] w-[20px] min-w-[20px]"
                  />
                  <p>Cancel Card</p>
                  {loadingCancel && (
                    <ClipLoader
                      color="#1D213B"
                      loading={loadingCancel}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
        {userRecord?.userType === Entity.Customer && (
          <>
            {!loadingCards ? (
              <div
                className={`relative flex h-full w-full flex-col items-center justify-center space-y-4 overflow-hidden ${cards && cards.length <= 1 && 'pt-4'}`}
              >
                {renderCardDisplay()}
              </div>
            ) : (
              <div className="flex h-[326px] w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingCards}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}

export default CardWidget;
